import "./FetchFilterBar.scss";
import { Category, FilterPaneState } from "@blisspointmedia/bpm-types/dist/FilterPane";
import { COMMON_CATEGORY_LABELS } from "./FilterPaneBasic";
import { Button, ButtonType, OverlayTrigger, Tooltip } from "../../Components";
import { StateSetter } from "../../utils/types";
import * as R from "ramda";
import React, { useCallback, useEffect, useState } from "react";

// Hardcoded styles that match the CSS - keep these in sync with SCSS
const CHIP_STYLES = {
  fontSize: "1rem",
  fontFamily: "Graphik",
  fontWeight: "500",
  paddingLeft: 16,
  paddingRight: 16,
};

const measureTextWidth = (text: string): number => {
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d");
  if (!context) {
    return 0;
  }

  context.font = `${CHIP_STYLES.fontWeight} ${CHIP_STYLES.fontSize} ${CHIP_STYLES.fontFamily}`;
  return context.measureText(text).width;
};

export interface FetchFilterBarChipProps {
  advanced?: string;
  category?: Category;
  disabled?: boolean;
  expanded?: boolean;
  filterState?: FilterPaneState;
  setInitialOpenSection?: StateSetter<string>;
  setShowEditFilterModal?: StateSetter<boolean>;
  containerRef?: React.RefObject<HTMLDivElement>;
}

export const FetchFilterBarChip: React.FC<FetchFilterBarChipProps> = React.memo(
  ({
    advanced,
    category,
    expanded,
    filterState,
    setInitialOpenSection = () => {},
    setShowEditFilterModal = () => {},
    containerRef,
  }) => {
    const [containerWidth, setContainerWidth] = useState<number>(0);
    useEffect(() => {
      const updateWidth = () => {
        if (containerRef?.current) {
          setContainerWidth(containerRef.current.offsetWidth);
        }
      };
      updateWidth();
      window.addEventListener("resize", updateWidth);
      return () => {
        window.removeEventListener("resize", updateWidth);
      };
    }, [containerRef]);

    const getItemsThatFit = useCallback(
      (items: string[], prefix: string, categoryLabel: string) => {
        // Use container width if available, otherwise fall back to window width calculation
        const effectiveWidth = containerWidth || window.innerWidth;
        const maxChipWidth = effectiveWidth * 0.3333;
        const categoryWidth = measureTextWidth(`${categoryLabel}:`);
        const prefixWidth = measureTextWidth(prefix);
        const moreTextWidth = measureTextWidth(" + 99 more");
        const padding = CHIP_STYLES.paddingLeft + CHIP_STYLES.paddingRight;
        const availableWidth = maxChipWidth - categoryWidth - prefixWidth - moreTextWidth - padding;
        let currentWidth = 0;
        let visibleCount = 0;
        for (let i = 0; i < items.length; i++) {
          const itemText = i === 0 ? items[i] : `, ${items[i]}`;
          const itemWidth = measureTextWidth(itemText);
          if (currentWidth + itemWidth > availableWidth) {
            break;
          }
          currentWidth += itemWidth;
          visibleCount++;
        }
        return {
          visibleItems: items.slice(0, visibleCount),
          hiddenCount: Math.max(0, items.length - visibleCount),
        };
      },
      [containerWidth]
    );

    const disabled =
      !R.isNil(category) && !R.isNil(category.key) && R.toLower(category.key) === "disabled";

    if (advanced) {
      const tokenCategory = <div className="tokenCategory">Advanced:</div>;
      const advancedContent = (
        <div className="tokenItems">{advanced.length ? advanced : "All"}</div>
      );
      const tokenContent = (
        <div className="tokenContent">
          {tokenCategory}
          {advancedContent}
        </div>
      );
      const chip = (
        <div className="tokenChip">
          <Button
            onClick={() => {
              setInitialOpenSection("Advanced");
              setShowEditFilterModal(true);
            }}
            type={ButtonType.OUTLINED}
            className={"tokenChipButton advanced"}
          >
            {tokenContent}
          </Button>
        </div>
      );

      return chip;
    } else if (category) {
      const tokenCategory = <div className="tokenCategory">{category.label}:</div>;
      const not =
        filterState && filterState.basic && filterState.basic.notMap
          ? filterState.basic.notMap[category.key]
          : false;
      const selectedTokenItems: string[] = [];
      if (filterState && filterState.basic && filterState.basic.selectedMap) {
        const { selectedMap } = filterState.basic;
        for (const selectedKey of R.keys(R.defaultTo({}, selectedMap[category.key]))) {
          if (selectedMap[category.key][selectedKey]) {
            if (
              COMMON_CATEGORY_LABELS[category.key] &&
              COMMON_CATEGORY_LABELS[category.key][selectedKey]
            ) {
              selectedTokenItems.push(COMMON_CATEGORY_LABELS[category.key][selectedKey]);
            } else {
              selectedTokenItems.push(selectedKey);
            }
          }
        }
      }
      const tokenItems = [...selectedTokenItems];
      const prefix = not ? "Exclude " : "";
      let contentString;
      const { visibleItems, hiddenCount } = getItemsThatFit(tokenItems, prefix, category.label);
      if (!expanded && hiddenCount > 0) {
        // Not expanded and not all/no items fit? Show count only
        contentString = `${not ? "Exclude" : ""} ${
          tokenItems.length
        } ${category.label.toLowerCase()}${hiddenCount === 1 ? "" : "s"}`;
      } else if (expanded) {
        if (hiddenCount > 0) {
          if (visibleItems.length > 0) {
            // Expanded and not all items fit? show items that fit + hidden count
            contentString = `${prefix}${visibleItems.join(", ")} + ${hiddenCount} more`;
          } else {
            // Expanded and no items fit? Show count only
            contentString = `${not ? "Exclude" : ""} ${
              tokenItems.length
            } ${category.label.toLowerCase()}${hiddenCount === 1 ? "" : "s"}`;
          }
        } else {
          // Expanded and all items fit? Show all items
          contentString = `${prefix}${visibleItems.join(", ")}`;
        }
      } else {
        // Not expanded and all items fit? Show all items
        contentString = `${prefix}${visibleItems.join(", ")}`;
      }

      const tokenItemsContent = <div className="tokenItems">{contentString}</div>;
      const tokenContent = (
        <div className="tokenContent">
          {tokenCategory}
          {tokenItemsContent}
        </div>
      );
      const chip = (
        <div className="tokenChip">
          <Button
            onClick={() => {
              if (!disabled) {
                setInitialOpenSection(category.key);
                setShowEditFilterModal(true);
              }
            }}
            type={ButtonType.OUTLINED}
            className={`tokenChipButton ${disabled ? "chipDisabled" : ""}`}
          >
            {tokenContent}
          </Button>
        </div>
      );

      const tooltipContent = (
        <Tooltip multiline={true} className="tokenChipTooltip">
          <div>
            {tokenItems.map((item, index) => (
              <div key={index}>{item}</div>
            ))}
          </div>
        </Tooltip>
      );

      if (disabled) {
        return (
          <OverlayTrigger
            overlay={
              <Tooltip>
                This filter is currently disabled with the current dimension settings.
              </Tooltip>
            }
          >
            {chip}
          </OverlayTrigger>
        );
      } else if (hiddenCount > 0) {
        return (
          <OverlayTrigger overlay={tooltipContent} placement="bottom left">
            {chip}
          </OverlayTrigger>
        );
      } else {
        return chip;
      }
    }
    return <div></div>;
  }
);

export default FetchFilterBarChip;
